import { useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, Clock, Users, Loader2, Wine, Minus, Plus, Pizza, Music, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FRIDAY_ARRIVAL_SLOTS, isFriday, toLocalDateStr, fmtDate } from "@/lib/booking";
import { useConfig } from "@/lib/useConfig";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Special event notices shown when a guest selects that date (keyed by YYYY-MM-DD).
const EVENT_NOTICES = {
  "2026-12-18": {
    title: "A special night at the cellar door",
    date: "Friday 18 December 2026",
    items: [
      { icon: Pizza, text: "Catering by YouPizza — woodfired pizza truck" },
      { icon: Music, text: "Live music by Wanita Ann Music" },
      { icon: Wine, text: "Gledswood serving up all your favourite drinks" },
    ],
  },
};

export default function TableBooking() {
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", party_size: 2, cheeseboards: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [eventNotice, setEventNotice] = useState(null);
  const { payments_enabled } = useConfig();
  const payOnArrival = !payments_enabled;

  useEffect(() => {
    api.get("/wine-bar/blocked-dates").then((r) => setBlockedDates(r.data.dates || [])).catch(() => {});
  }, []);

  const selectedNotice = date ? EVENT_NOTICES[toLocalDateStr(date)] : null;

  const cheeseNum = Number(form.cheeseboards) || 0;
  const cheeseCost = cheeseNum * 42;
  const setCheeseboards = (n) => setForm({ ...form, cheeseboards: Math.max(0, n) });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const disabledMatcher = (d) => d < today || !isFriday(d) || blockedDates.includes(toLocalDateStr(d));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !slot) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        party_size: Number(form.party_size),
        cheeseboards: Number(form.cheeseboards),
        date: toLocalDateStr(date),
        arrival_time: slot,
        origin_url: window.location.origin,
      };
      const res = await api.post("/payments/create-table-checkout", payload);
      if (res.data.payment_required === false) {
        window.location.href = `/table/success?token=${res.data.manage_token}`;
      } else {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not confirm your booking.");
      setSubmitting(false);
    }
  };

  return (
    <section id="dine" data-testid="table-booking-section" className="py-24 md:py-32 bg-[#1C1917]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm uppercase tracking-[0.25em] font-semibold text-[#D4B24C]">Friday Nights · 6pm–11pm</p>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white">
            The Friday Night Wine Bar
          </h2>
          <p className="mt-4 text-white/70 text-lg">
            The cellar door stays open as a wine bar every Friday night, 6pm–11pm.
            Reserve your spot, choose an arrival time, and add a grazing cheeseboard.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left: calendar + arrival times */}
          <div className="lg:col-span-5 bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-[#E7E5E4]">
            <div className="flex items-center gap-2 mb-4 text-[#1C1917] font-medium">
              <CalendarDays className="h-5 w-5 text-[#722F37]" /> Select a Friday
            </div>
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => {
                setDate(d);
                setSlot(null);
                if (d) {
                  const notice = EVENT_NOTICES[toLocalDateStr(d)];
                  if (notice) {
                    setEventNotice(notice);
                    setNoticeOpen(true);
                  }
                }
              }}
              disabled={disabledMatcher}
              data-testid="table-calendar"
              className="rounded-xl"
            />
            <p className="mt-3 text-xs text-[#57534E]">Table service is available on Friday nights only.</p>

            {date && (
              <div className="mt-6 animate-fade-up">
                <div className="flex items-center gap-2 mb-3 text-[#1C1917] font-medium">
                  <Clock className="h-5 w-5 text-[#722F37]" /> Arrival time
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {FRIDAY_ARRIVAL_SLOTS.map((t) => (
                    <button
                      key={t}
                      data-testid={`arrival-slot-${t.replace(/[:\s]/g, "").toLowerCase()}`}
                      onClick={() => setSlot(t)}
                      className={`py-3 px-2 rounded-xl border text-sm font-medium transition-all ${
                        slot === t
                          ? "bg-[#722F37] text-white border-[#722F37]"
                          : "border-[#E7E5E4] text-[#57534E] hover:border-[#722F37] hover:text-[#722F37]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7 bg-white p-6 md:p-10 rounded-2xl shadow-sm border border-[#E7E5E4]">
            {!date || !slot ? (
              <div className="h-full min-h-[380px] flex flex-col items-center justify-center text-center">
                <div className="h-14 w-14 rounded-full bg-[#722F37]/10 flex items-center justify-center mb-4">
                  <Wine className="h-7 w-7 text-[#722F37]" />
                </div>
                <h3 className="font-serif text-2xl text-[#1C1917]">Your wine bar booking</h3>
                <p className="mt-2 text-[#57534E] max-w-sm">
                  Pick a Friday and an arrival time on the left, then tell us about your group.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="animate-fade-up" data-testid="table-form">
                <div className="flex items-center gap-2 mb-6 text-sm bg-[#FDFBF7] border border-[#E7E5E4] rounded-xl px-4 py-3">
                  <CalendarDays className="h-4 w-4 text-[#722F37]" />
                  <span className="font-medium text-[#1C1917]">{fmtDate(date)}</span>
                  <span className="text-[#57534E]">·</span>
                  <Clock className="h-4 w-4 text-[#722F37]" />
                  <span className="font-medium text-[#1C1917]">Arrive {slot}</span>
                </div>

                {selectedNotice && (
                  <div className="mb-6 rounded-xl border border-[#D4B24C]/40 bg-[#D4B24C]/10 p-4" data-testid="event-notice-banner">
                    <div className="flex items-center gap-2 text-[#8a6d12] font-semibold text-sm">
                      <PartyPopper className="h-4 w-4" /> Special event this night
                    </div>
                    <ul className="mt-2 space-y-1.5">
                      {selectedNotice.items.map((it) => (
                        <li key={it.text} className="flex items-center gap-2 text-sm text-[#57534E]">
                          <it.icon className="h-4 w-4 text-[#722F37] shrink-0" /> {it.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold mb-2 text-[#1C1917]">Full name</label>
                    <input
                      required
                      data-testid="table-input-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Jane Winemaker"
                      className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] bg-white focus:ring-2 focus:ring-[#722F37]/20 focus:border-[#722F37] transition-colors outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-[#1C1917]">Phone</label>
                    <input
                      required
                      data-testid="table-input-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="0400 000 000"
                      className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] bg-white focus:ring-2 focus:ring-[#722F37]/20 focus:border-[#722F37] transition-colors outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-[#1C1917]">Email</label>
                    <input
                      required
                      type="email"
                      data-testid="table-input-email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="jane@email.com"
                      className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] bg-white focus:ring-2 focus:ring-[#722F37]/20 focus:border-[#722F37] transition-colors outline-none"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold mb-2 text-[#1C1917]">
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-4 w-4" /> Number of people in your group
                      </span>
                    </label>
                    <input
                      required
                      type="number"
                      min="1"
                      max="50"
                      data-testid="table-input-party"
                      value={form.party_size}
                      onChange={(e) => setForm({ ...form, party_size: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] bg-white focus:ring-2 focus:ring-[#722F37]/20 focus:border-[#722F37] transition-colors outline-none"
                    />
                  </div>
                </div>

                <div className="mt-5 rounded-xl border border-[#4A5D23]/25 bg-[#4A5D23]/5 p-4" data-testid="table-cheeseboard-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-[#1C1917] text-sm">Add a cheeseboard</p>
                      <p className="text-xs text-[#57534E]">$42.00 each · serves two · settled on arrival</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        data-testid="table-cheese-minus"
                        onClick={() => setCheeseboards(cheeseNum - 1)}
                        className="h-9 w-9 rounded-full border border-[#E7E5E4] flex items-center justify-center text-[#57534E] hover:border-[#722F37] hover:text-[#722F37] transition-colors disabled:opacity-40"
                        disabled={cheeseNum <= 0}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-6 text-center font-semibold text-[#1C1917]" data-testid="table-cheese-count">{cheeseNum}</span>
                      <button
                        type="button"
                        data-testid="table-cheese-plus"
                        onClick={() => setCheeseboards(cheeseNum + 1)}
                        className="h-9 w-9 rounded-full border border-[#E7E5E4] flex items-center justify-center text-[#57534E] hover:border-[#722F37] hover:text-[#722F37] transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-5 rounded-xl border border-[#722F37]/20 bg-[#722F37]/5 p-4 text-sm" data-testid="table-deposit-note">
                  {payOnArrival ? (
                    <>
                      {cheeseNum > 0 && (
                        <div className="flex justify-between text-[#57534E] mb-1.5">
                          <span>Cheeseboards · $42.00 × {cheeseNum}</span>
                          <span className="font-medium text-[#1C1917]">${cheeseCost.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold text-[#1C1917]">
                        <span>{cheeseNum > 0 ? "Due on arrival" : "Payment"}</span>
                        <span data-testid="table-amount-due">{cheeseNum > 0 ? `$${cheeseCost.toFixed(2)}` : "Pay at the bar"}</span>
                      </div>
                      <p className="mt-1 text-xs text-[#57534E]">
                        No payment is required now. Table reserved and held for 30 minutes.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between text-[#57534E]">
                        <span>Booking deposit</span>
                        <span className="font-medium text-[#1C1917]">$50.00</span>
                      </div>
                      {cheeseNum > 0 && (
                        <div className="flex justify-between text-[#57534E] mt-1.5">
                          <span>Cheeseboards · $42.00 × {cheeseNum}</span>
                          <span className="font-medium text-[#1C1917]">${cheeseCost.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between mt-2 pt-2 border-t border-[#722F37]/15 font-semibold text-[#1C1917]">
                        <span>Pay now</span>
                        <span>${(50 + cheeseCost).toFixed(2)}</span>
                      </div>
                      <p className="mt-1 text-xs text-[#57534E]">
                        The $50 deposit comes off your final bill — the balance is payable on the night.
                      </p>
                    </>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-table-btn"
                  className="mt-5 w-full py-4 px-8 bg-[#722F37] text-white rounded-full font-semibold text-lg hover:opacity-90 transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" /> {payOnArrival ? "Confirming your booking…" : "Redirecting to secure checkout…"}
                    </>
                  ) : (
                    <>{payOnArrival ? "Confirm Booking" : `Pay $${(50 + cheeseCost).toFixed(2)} & Confirm`}</>
                  )}
                </button>
                <p className="mt-3 text-center text-xs text-[#57534E]">
                  {payOnArrival
                    ? "No payment now — payment is collected on arrival at the cellar door."
                    : "Secure payment powered by Stripe. Tables can't be held without a deposit."}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      <Dialog open={noticeOpen} onOpenChange={setNoticeOpen}>
        <DialogContent data-testid="event-notice-modal" className="max-w-md bg-[#FDFBF7] border-[#E7E5E4]">
          <DialogHeader>
            <div className="h-12 w-12 rounded-full bg-[#722F37]/10 flex items-center justify-center mb-2">
              <PartyPopper className="h-6 w-6 text-[#722F37]" />
            </div>
            <DialogTitle className="font-serif text-2xl text-[#1C1917]">{eventNotice?.title}</DialogTitle>
            <DialogDescription className="text-[#722F37] font-medium">
              {eventNotice?.date} — this night only
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-3 mt-1">
            {eventNotice?.items.map((it) => (
              <li key={it.text} className="flex items-start gap-3 text-[#1C1917]">
                <span className="h-9 w-9 rounded-full bg-white border border-[#E7E5E4] flex items-center justify-center shrink-0">
                  <it.icon className="h-5 w-5 text-[#722F37]" />
                </span>
                <span className="pt-1.5 text-sm">{it.text}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setNoticeOpen(false)}
            data-testid="event-notice-close"
            className="mt-2 w-full py-3 px-6 bg-[#722F37] text-white rounded-full font-semibold hover:bg-[#5E262D] transition-colors"
          >
            Sounds great — continue booking
          </button>
        </DialogContent>
      </Dialog>
    </section>
  );
}
